import { colors } from '../../theme/colors';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Divider,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import React from "react";

const getStatusColor = (status) => {
  switch (status) {
    case "Completed":
      return { bg: "#e8f5e9", color: "#2e7d32" };
    case "Scheduled":
      return { bg: "#e3f2fd", color: "#1565c0" };
    case "Cancelled":
      return { bg: "#ffebee", color: "#c62828" };
    default:
      return { bg: "#f5f5f5", color: "#616161" };
  }
};

const dialogSx = {
  "& .MuiBackdrop-root": {
    backgroundColor: "rgba(0, 0, 0, 0.3)",
    boxShadow: "none",
  },
};

const paperSx = {
  boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.2)",
  borderRadius: "12px",
};

const titleSx = {
  color: colors.primary,
  fontWeight: 600,
  pb: 1,
};

const contentSx = { pt: 2, overflowX: "hidden" };

const infoTextSx = { color: "#666", mb: 2, lineHeight: 1.6 };

const tableContainerSx = {
  border: "1px solid #e0e0e0",
  borderRadius: "8px",
  maxHeight: 280,
  overflow: "auto",
};

const headerCellSx = {
  fontWeight: 600,
  color: colors.primary,
  fontSize: "0.8rem",
  borderBottom: `2px solid ${colors.primary}`,
  whiteSpace: "nowrap",
  py: 1.2,
};

const bodyCellSx = {
  fontSize: "0.8rem",
  color: "#333",
  py: 1.2,
  borderBottom: "1px solid #e0e0e0",
};

const rowSx = {
  "&:last-child td": { borderBottom: 0 },
  "&:hover": { backgroundColor: "#f8f9fa" },
};

const actionsSx = { padding: "1rem", gap: "0.5rem" };

const cancelBtnSx = {
  backgroundColor: "#fff",
  borderColor: colors.primary,
  color: colors.primary,
  textTransform: "capitalize",
  ":hover": {
    backgroundColor: "#fff",
    borderColor: colors.primary,
  },
};

const continueBtnSx = (loading) => ({
  backgroundColor: colors.primary,
  color: "#fff",
  textTransform: "capitalize",
  ":hover": {
    backgroundColor: colors.primary,
    color: "#fff",
  },
  minWidth: loading ? "160px" : "auto",
});

const loadingBoxSx = { display: "flex", alignItems: "center" };
const spinnerSx = { marginLeft: 1 };

const DuplicateEmailModal = ({
  open,
  handleClose,
  handleContinue,
  duplicateData,
  loading,
}) => {
  if (
    !duplicateData ||
    !Array.isArray(duplicateData) ||
    duplicateData.length === 0
  )
    return null;

  return (
    <Dialog
      open={open}
      onClose={!loading ? handleClose : undefined}
      aria-labelledby="duplicate-email-dialog-title"
      maxWidth="md"
      fullWidth
      sx={dialogSx}
      PaperProps={{ sx: paperSx }}
    >
      <DialogTitle sx={titleSx} id="duplicate-email-dialog-title">
        Existing Interviews Found
      </DialogTitle>

      <Divider />

      <DialogContent sx={contentSx}>
        <Typography variant="body2" sx={infoTextSx}>
          The following interview(s) already exist for this email address. You
          can still proceed to schedule a new interview.
        </Typography>

        <TableContainer sx={tableContainerSx}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Recruiter Name</TableCell>
                <TableCell sx={headerCellSx}>Recruiter Email</TableCell>
                <TableCell sx={headerCellSx}>Time</TableCell>
                <TableCell sx={headerCellSx}>Interview Type</TableCell>
                <TableCell sx={headerCellSx}>Status</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {duplicateData.map((item, index) => {
                const statusColor = getStatusColor(item.interviewStatus);
                const typeConfig = {
                  "AI Bot": { bg: "#e3f2fd", color: "#1565c0" },
                  "Coding Test": { bg: "#e8f5e9", color: "#2e7d32" },
                  "AI Bot +": { bg: "#f3e5f5", color: "#7b1fa2" },
                };
                const typeStyle = typeConfig[item.interviewType] || {
                  bg: "#f5f5f5",
                  color: "#616161",
                };
                const typeLabel =
                  item.interviewType === "AI Bot +"
                    ? "AI Bot +"
                    : item.interviewType === "Coding Test"
                    ? "Coding Assessment"
                    : "AI Interview";

                return (
                  <TableRow key={index} sx={rowSx}>
                    <TableCell sx={bodyCellSx}>
                      {item.recruiter || "N/A"}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      {item.recruiterEmail || "N/A"}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      {item.startTime
                        ? new Date(item.startTime).toLocaleString()
                        : "N/A"}
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Chip
                        label={typeLabel}
                        size="small"
                        sx={{
                          backgroundColor: typeStyle.bg,
                          color: typeStyle.color,
                          fontWeight: 600,
                          fontSize: "0.72rem",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Chip
                        label={item.interviewStatus}
                        size="small"
                        sx={{
                          backgroundColor: statusColor.bg,
                          color: statusColor.color,
                          fontWeight: 600,
                          fontSize: "0.72rem",
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography
          variant="body2"
          sx={{ color: "#666", mt: 2, lineHeight: 1.6 }}
        >
          Do you still want to schedule a new interview for this candidate?
        </Typography>
      </DialogContent>

      <DialogActions sx={actionsSx}>
        <Button
          variant="outlined"
          sx={cancelBtnSx}
          onClick={handleClose}
          disabled={loading}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleContinue}
          autoFocus
          disabled={loading}
          sx={continueBtnSx(loading)}
        >
          {loading ? (
            <Box sx={loadingBoxSx}>
              Scheduling
              <CircularProgress size={20} color="inherit" sx={spinnerSx} />
            </Box>
          ) : (
            "Schedule Anyway"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateEmailModal;
